import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Page from "../components/ui/Page";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import { getWorkspaceId, isAuthenticated } from "../services/authStorage";

type ExhibitHit = {
  id: string;
  bates: string;
  filename: string;
  witness: string;
  snippet: string;
  confidence: number;
  anchor: { page: number; line: number };
};

const fixtureHits: ExhibitHit[] = [
  {
    id: "hit-1",
    bates: "LEX-00012001",
    filename: "Budget_Review_Notes_2024-02-05.pdf",
    witness: "Marcus Venn",
    snippet: "...budget review occurred Feb 5, 2024 with CFO present...",
    confidence: 94,
    anchor: { page: 3, line: 14 }
  },
  {
    id: "hit-2",
    bates: "LEX-00012018",
    filename: "Email_2024-01-05_Travel_Receipt.pdf",
    witness: "Evelyn Brooks",
    snippet: "...flight itinerary shows Detroit on Jan 5, 2024...",
    confidence: 88,
    anchor: { page: 1, line: 6 }
  },
  {
    id: "hit-3",
    bates: "LEX-00012044",
    filename: "Compliance_Notice_Jan2024.pdf",
    witness: "Tanya Ruiz",
    snippet: "...notification sent to Tanya Ruiz at 08:22 AM...",
    confidence: 81,
    anchor: { page: 2, line: 9 }
  }
];

const fixtureDocLines = [
  "Budget review summary prepared for CFO review.",
  "Attendees: Marcus Venn, Evelyn Brooks, Legal Ops.",
  "Review window: Feb 5, 2024, 08:00 AM local.",
  "Expense variance noted in travel receipts.",
  "Reference: Detroit itinerary dated Jan 5, 2024.",
  "Compliance notice issued at 08:22 AM to Tanya Ruiz.",
  "Follow-up required before approval execution.",
  "Appendix includes email headers and metadata.",
  "Custodian notes: preserve native exports."
];

export default function WitnessIntelligence() {
  const navigate = useNavigate();
  const workspaceId = useMemo(() => getWorkspaceId(), []);
  const authed = isAuthenticated();
  const [query, setQuery] = useState("");
  const fixtureMode = useMemo(() => {
    if (import.meta.env.DEV) return true;
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return params.get("fixture") === "1";
  }, []);
  const [results, setResults] = useState<ExhibitHit[]>(fixtureMode ? fixtureHits : []);
  const [active, setActive] = useState<ExhibitHit | null>(fixtureMode ? fixtureHits[0] : null);
  const [busy, setBusy] = useState(false);

  const runQuery = () => {
    setBusy(true);
    setActive(null);
    window.setTimeout(() => {
      if (!fixtureMode) {
        setResults([]);
        setActive(null);
        setBusy(false);
        return;
      }
      const filtered = fixtureHits.filter((hit) =>
        hit.snippet.toLowerCase().includes(query.toLowerCase()) ||
        hit.filename.toLowerCase().includes(query.toLowerCase()) ||
        hit.witness.toLowerCase().includes(query.toLowerCase())
      );
      const nextResults = filtered.length ? filtered : fixtureHits;
      setResults(nextResults);
      setActive(nextResults[0] ?? null);
      setBusy(false);
    }, 450);
  };

  const topWitness = useMemo(() => results[0]?.witness || "Awaiting query", [results]);
  const highlightLine = active?.anchor.line ?? 6;
  const highlightPage = active?.anchor.page ?? 1;
  const piiFindings = useMemo(() => ([
    {
      id: "pii-1",
      label: "SSN pattern detected",
      location: "Line 18",
      status: "REDACT"
    },
    {
      id: "pii-2",
      label: "Email address: evelyn.brooks@firm.com",
      location: "Line 22",
      status: "MASK"
    },
    {
      id: "pii-3",
      label: "Phone number: (313) 555-0192",
      location: "Line 24",
      status: "MASK"
    }
  ]), [active]);

  if (!authed || !workspaceId) {
    return (
      <Page
        title="Intelligence Hub"
        subtitle="Conflict triage, witness impeachment, and PII shielding in one workspace."
      >
        <div className="rounded-3xl border border-white/10 bg-slate-950/70 p-8 text-center">
          <div className="text-xs uppercase tracking-[0.4em] text-slate-500">Run Analysis</div>
          <h2 className="mt-4 text-2xl font-semibold text-white">Surface conflicts and anchor testimony.</h2>
          <p className="mt-3 text-sm text-slate-400">
            Evidence-bound analysis keeps witness intelligence defensible under governance scrutiny.
          </p>
          <div className="mt-6 flex items-center justify-center">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                if (!authed) {
                  navigate("/login");
                  return;
                }
                navigate("/intelligence");
              }}
            >
              Run analysis
            </Button>
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page
      title="Intelligence Hub"
      subtitle="Conflict triage, witness impeachment, and PII shielding in one workspace."
      right={
        <div className="flex items-center gap-2">
          <Badge tone="green">Conflict Mode</Badge>
          <Badge tone="amber">PII Shield</Badge>
        </div>
      }
    >
      {fixtureMode ? (
        <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-amber-200">
          Fixture dataset loaded (evaluation harness).
        </div>
      ) : null}
      <div className="rounded-2xl border border-white/10 bg-slate-950/70 p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-xs uppercase tracking-[0.4em] text-slate-400">Discovery Query</div>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Where did he mention the 2024 budget?'
            className="flex-1 rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-xs text-slate-200 font-mono placeholder:text-slate-500"
          />
          <Button variant="primary" size="sm" onClick={runQuery} disabled={busy}>
            {busy ? "Scanning..." : "Run Query"}
          </Button>
        </div>
        <div className="mt-3 text-xs text-slate-400">
          Top Witness: <span className="text-emerald-200">{topWitness}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <section className="rounded-2xl border border-white/10 bg-slate-950/70 p-4 lexi-sector">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.4em] text-slate-400">Document OCR</div>
            <Badge tone="blue">Sniper Overlay</Badge>
          </div>
          <div className="mt-4 rounded-2xl border border-white/10 bg-black/60 p-3">
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>{active ? active.filename : "Select an exhibit"}</span>
              <span className="font-mono text-emerald-200">{active?.bates ?? "--"}</span>
            </div>
            <div className="mt-3 max-h-72 overflow-auto space-y-1 font-mono text-[11px]">
              {fixtureDocLines.map((line, idx) => {
                const lineNumber = idx + 1;
                const isHighlight = lineNumber === highlightLine;
                return (
                  <div
                    key={`${line}-${lineNumber}`}
                    className={[
                      "flex gap-3 rounded px-2 py-1",
                      isHighlight ? "border border-emerald-500/30 bg-emerald-500/10 text-emerald-200" : "text-slate-300"
                    ].join(" ")}
                  >
                    <span className="w-6 text-right text-slate-600">{String(lineNumber).padStart(2, "0")}</span>
                    <span>{line}</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 text-[10px] text-slate-500">
              Anchor: Page {highlightPage}, Line {highlightLine}
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-white/10 bg-slate-950/70 p-4 lexi-sector">
          <div className="text-xs uppercase tracking-[0.4em] text-slate-400">Conflict Triage</div>
          <div className="mt-4 space-y-3">
            {results.length ? results.map((hit) => (
              <button
                key={hit.id}
                onClick={() => setActive(hit)}
                className={`w-full rounded-2xl border px-4 py-3 text-left transition-colors ${
                  active?.id === hit.id ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10 bg-black/40 hover:border-emerald-500/30"
                }`}
                type="button"
              >
                <div className="flex items-center justify-between text-xs text-slate-300">
                  <span className="font-mono text-emerald-200">{hit.bates}</span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Confidence {hit.confidence}%</span>
                </div>
                <div className="mt-2 text-sm text-slate-100">{hit.filename}</div>
                <div className="mt-2 text-xs text-slate-400">Witness: {hit.witness}</div>
                <div className="mt-2 text-xs text-slate-300 italic">"{hit.snippet}"</div>
              </button>
            )) : (
              <div className="text-xs text-slate-500">No matches yet. Run a query to populate results.</div>
            )}
          </div>
        </section>

        <aside className="rounded-2xl border border-white/10 bg-slate-950/70 p-4 lexi-sector">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.4em] text-slate-400">PII Shield</div>
            <Badge tone="amber">Auto Scan</Badge>
          </div>
          <div className="mt-4 space-y-3">
            {piiFindings.map((item) => (
              <div key={item.id} className="rounded-2xl border border-white/10 bg-black/50 p-3 text-xs text-slate-200">
                <div className="flex items-center justify-between">
                  <span>{item.label}</span>
                  <span className={`text-[10px] uppercase tracking-[0.2em] ${item.status === "REDACT" ? "text-red-200" : "text-amber-200"}`}>
                    {item.status}
                  </span>
                </div>
                <div className="mt-2 font-mono text-[10px] text-slate-500">Location: {item.location}</div>
              </div>
            ))}
          </div>
          <Button variant="primary" size="sm" className="mt-4 w-full">
            Bulk Redact
          </Button>
          <div className="mt-3 text-[10px] text-slate-500">
            Export will include a privilege log and masked overlay.
          </div>
        </aside>
      </div>
    </Page>
  );
}
